import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import {
    mdiCurrencyUsd,
    mdiEmailOutline,
    // mdiContentCopy, // Reserved — re-enable when Copy Link button is restored
    mdiQrcode,
    mdiAccountCashOutline,
} from "@mdi/js";
import QRCode from "qrcode";
import { toast } from "react-toastify";
import BaseModal from "./BaseModal";
import MarkPaidActions from "./classes/MarkPaidActions";
import { bookingApi } from "../api/booking";
import { billingApi } from "../api/billing";
import { formatCurrencyFromMinor } from "../utils/currency";
import usePayoutsReady from "../hooks/usePayoutsReady";
import "./BookingPaymentModal.css";

/**
 * BookingPaymentModal — instructor "Request Payment" flow for a confirmed unpaid booking.
 *
 * Mirrors AdminPaymentModal's visual treatment + conditional bank-transfer / Mark paid
 * behaviour. Collapsed step machine: summary → qr (Mark paid handled via inline confirm
 * in the summary step). Every generate / send call mints a fresh token and invalidates
 * any prior one for this booking.
 */
function BookingPaymentModal({ isOpen, onClose, booking, onSuccess, startInMarkPaid = false }) {
    const [step, setStep] = useState("summary");
    const [useCustom, setUseCustom] = useState(false);
    const [customAmount, setCustomAmount] = useState("");
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [qrInfo, setQrInfo] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isMarkingPaid, setIsMarkingPaid] = useState(false);
    const [bankTransferEnabled, setBankTransferEnabled] = useState(false);
    const [confirmMarkPaid, setConfirmMarkPaid] = useState(false);
    // Locally-tracked expiry from any generate/send call in THIS modal session.
    // Overrides booking.paymentQrExpiresAt for the banner so a fresh mint shows
    // the new expiry without waiting for the parent to refetch.
    const [localTokenExpiresAt, setLocalTokenExpiresAt] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setStep("summary");
            setUseCustom(false);
            setCustomAmount("");
            setQrDataUrl(null);
            setQrInfo(null);
            setIsGenerating(false);
            setIsSendingEmail(false);
            setIsMarkingPaid(false);
            setConfirmMarkPaid(false);
            setLocalTokenExpiresAt(null);
            return;
        }
        // Opened via the booking-detail "Mark paid" action → jump straight to the
        // method picker (Mark paid is a distinct intent from Request Payment / sending a link).
        if (startInMarkPaid) setConfirmMarkPaid(true);
        let cancelled = false;
        (async () => {
            try {
                const state = await billingApi.getAddonState();
                if (!cancelled) setBankTransferEnabled(!!state?.offeringEnabled);
            } catch {
                if (!cancelled) setBankTransferEnabled(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    // false = positively not able to take card payments (no active Stripe account);
    // null = unknown (loading/fetch failed) — never block on unknown.
    const payoutsReady = usePayoutsReady(booking?.instructorId, isOpen);
    const cardPaymentsBlocked = payoutsReady === false;

    if (!booking) return null;

    const currency = booking.pricing?.currency || "AUD";
    // Server-computed display pricing (effectivePricing) — THE single source of
    // truth. These are the same numbers the QR / payment page / email will show,
    // because every surface renders the one backend calculator. Never compute
    // booking prices client-side.
    const ep = booking.effectivePricing || {};
    const lessonFee = ep.lessonFee || 0;
    const venueFee = ep.venueFee || 0;
    const creditApplied = ep.creditApplied || 0;
    const serviceFee = ep.serviceFee || 0;
    const total = ep.total || 0;
    const studentName =
        booking.studentName ||
        [booking.studentInfo?.firstName, booking.studentInfo?.lastName].filter(Boolean).join(" ") ||
        "Student";
    const recipientEmail = booking.studentInfo?.email;

    const parseOverride = () => {
        if (!useCustom) return undefined;
        const raw = String(customAmount).trim();
        if (raw === "") return undefined;
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
            toast.error("Custom amount must be a non-negative number.");
            return null;
        }
        // The input is dollar-denominated ($ prefix); the backend override is in
        // integer cents (matches pricing.customAmountOverride). Convert here.
        return Math.round(n * 100);
    };

    const handleGenerateQr = async () => {
        const override = parseOverride();
        if (override === null) return;
        try {
            setIsGenerating(true);
            const result = await bookingApi.generatePaymentQr(booking.id, override);
            const dataUrl = await QRCode.toDataURL(result.qrUrl, { width: 480, margin: 1 });
            setQrDataUrl(dataUrl);
            setQrInfo(result);
            setLocalTokenExpiresAt(result.qrExpiresAt);
            setStep("qr");
            if (typeof onSuccess === "function") onSuccess();
        } catch (err) {
            toast.error(err?.message || "Failed to generate QR code.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSendEmail = async (closeAfter = true) => {
        if (!recipientEmail) {
            toast.error("This booking has no email on file. Edit the booking to add one.");
            return;
        }
        const override = parseOverride();
        if (override === null) return;
        try {
            setIsSendingEmail(true);
            const result = await bookingApi.sendPaymentEmail(booking.id, override);
            // sendPaymentEmail also mints a fresh token server-side. We don't get
            // the new expiry back today, but we know it's now + 24h. Update local
            // expiry to that so the banner reflects the new state immediately.
            setLocalTokenExpiresAt(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString());
            toast.success(`Payment email sent to ${result.email}`);
            if (typeof onSuccess === "function") onSuccess();
            if (closeAfter) onClose();
        } catch (err) {
            toast.error(err?.message || "Failed to send payment email.");
        } finally {
            setIsSendingEmail(false);
        }
    };

    // Copy Link handler — reserved for future. No AC required it and AdminPaymentModal
    // doesn't have it, so the button was removed pending a real user-story request.
    // eslint-disable-next-line no-unused-vars
    const _handleCopyLink = async () => {
        if (!qrInfo?.qrUrl) return;
        try {
            await navigator.clipboard.writeText(qrInfo.qrUrl);
            toast.success("Payment link copied");
        } catch {
            toast.error("Could not copy to clipboard.");
        }
    };

    const handleMarkPaid = async (method = "bank_transfer", notes = null) => {
        try {
            setIsMarkingPaid(true);
            await bookingApi.markBankTransfer(booking.id, method, notes);
            toast.success("Booking marked as paid.");
            if (typeof onSuccess === "function") onSuccess();
            onClose();
        } catch (err) {
            toast.error(err?.message || "Failed to mark booking as paid.");
        } finally {
            setIsMarkingPaid(false);
            setConfirmMarkPaid(false);
        }
    };

    const renderBody = () => {
        if (step === "qr" && qrDataUrl && qrInfo) {
            return (
                <div className="booking-payment-modal-qr">
                    <p className="booking-payment-modal-hint-text">
                        Ask the student to scan this with their phone. Easiest if they have <strong>Apple Pay</strong>{" "}
                        or <strong>Google Pay</strong> ready.
                    </p>
                    <p className="booking-payment-modal-qr-amount">
                        {formatCurrencyFromMinor(qrInfo.totalDue, qrInfo.currency)}
                    </p>
                    <img src={qrDataUrl} alt="Booking payment QR code" className="booking-payment-modal-qr-image" />
                    <p className="booking-payment-modal-qr-hint">
                        Student scans this QR to pay.
                        {qrInfo.qrExpiresAt
                            ? ` Valid until ${new Date(qrInfo.qrExpiresAt).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}.`
                            : ""}
                    </p>
                </div>
            );
        }

        if (confirmMarkPaid) {
            return (
                <div className="booking-payment-modal-summary">
                    <p className="booking-payment-modal-hint-text">
                        Choose how you received payment. This records the booking as paid outside the platform; all
                        methods are settled the same way and can be reconciled later. The student will not receive any
                        further payment emails.
                    </p>
                    <div className="booking-payment-modal-total">
                        {/* External payment = no card checkout, so no service fee — the
                            amount received is the principal (lesson + venue) minus any
                            credit already applied to this booking. */}
                        <span>Amount marked paid</span>
                        <span>{formatCurrencyFromMinor((ep.principal || 0) - (ep.creditApplied || 0), currency)}</span>
                    </div>
                    {/* Reuse the class-domain rail picker (Bank transfer / EFTPOS / Cash / Other + notes)
                        so the booking flow mirrors class markExternalPayment exactly. */}
                    <MarkPaidActions onMarkPaid={handleMarkPaid} isSubmitting={isMarkingPaid} />
                </div>
            );
        }

        // Show "no payment due yet" hint only when the booking is set to pay
        // after-lesson AND the lesson hasn't happened yet. After the date,
        // the lesson IS done and payment IS due — the hint would be wrong.
        const bookingDate = booking.bookingDate ? new Date(booking.bookingDate) : null;
        const lessonInFuture = bookingDate ? bookingDate >= new Date(new Date().setHours(0, 0, 0, 0)) : false;
        const isAfterLesson = booking.pricing?.paymentTiming === 'after-lesson' && lessonInFuture;
        // Prior-token state: token exists, still within 24h TTL. Prefer the
        // locally-tracked expiry from THIS modal session (so a fresh mint
        // shows up immediately) and fall back to the booking prop.
        const effectiveExpiryStr = localTokenExpiresAt || booking.paymentQrExpiresAt;
        const priorTokenExpiry = effectiveExpiryStr ? new Date(effectiveExpiryStr) : null;
        const hasToken = !!localTokenExpiresAt || !!booking.paymentQrToken;
        const priorTokenStillValid =
            hasToken && priorTokenExpiry && priorTokenExpiry > new Date();
        return (
            <div className="booking-payment-modal-summary">
                {isAfterLesson && (
                    <div className="booking-payment-modal-info-banner">
                        <strong>NOTE:</strong> Your booking settings are set to payment after the lesson, so no payment
                        is due yet. You can still issue a link now if you want to.
                    </div>
                )}

                {priorTokenStillValid && (
                    <div className="booking-payment-modal-status-banner">
                        <span className="booking-payment-modal-status-banner-dot" />
                        <span>
                            Payment link issued, awaiting payment (expires{" "}
                            {priorTokenExpiry.toLocaleString("en-AU", {
                                day: "numeric",
                                month: "long",
                                hour: "numeric",
                                minute: "2-digit",
                            })}
                            )
                        </span>
                    </div>
                )}

                {cardPaymentsBlocked && (
                    <div className="booking-payment-modal-info-banner">
                        <strong>Online payments are not set up yet.</strong> Connect your payout account in{" "}
                        <a href="/manage-finances">Manage Finances</a> to send card payment requests. You can still
                        use Mark paid to record a payment received outside the platform.
                    </div>
                )}
                {!cardPaymentsBlocked && (
                    <>
                        <p className="booking-payment-modal-hint-text">
                            <strong>Pay now (QR)</strong> shows a QR for instant pay on the student&apos;s phone (best with Apple
                            Pay or Google Pay).
                        </p>
                        <p className="booking-payment-modal-hint-text">
                            <strong>OK (send email)</strong> emails a pay-later link
                            {bankTransferEnabled ? ", including your bank transfer details," : ""} so the student pays in their
                            own time.
                        </p>
                    </>
                )}
                {bankTransferEnabled && (
                    <p className="booking-payment-modal-hint-text">
                        <strong>Mark paid</strong> if you&apos;ve already received payment by bank transfer, EFTPOS, or
                        cash.
                    </p>
                )}

                {recipientEmail && (
                    <div className="booking-payment-modal-pricing-row">
                        <span>Email</span>
                        <span>{recipientEmail}</span>
                    </div>
                )}

                <div className="booking-payment-modal-pricing-row">
                    <span>Lesson fee</span>
                    <span>{formatCurrencyFromMinor(lessonFee, currency)}</span>
                </div>
                {venueFee > 0 && (
                    <div className="booking-payment-modal-pricing-row">
                        <span>Venue fee</span>
                        <span>{formatCurrencyFromMinor(venueFee, currency)}</span>
                    </div>
                )}
                {creditApplied > 0 && (
                    <div className="booking-payment-modal-pricing-row">
                        <span>Credit applied</span>
                        <span>-{formatCurrencyFromMinor(creditApplied, currency)}</span>
                    </div>
                )}
                {serviceFee > 0 && (
                    <div className="booking-payment-modal-pricing-row">
                        <span>Service fee</span>
                        <span>{formatCurrencyFromMinor(serviceFee, currency)}</span>
                    </div>
                )}

                <div className="booking-payment-modal-total">
                    <span>Total</span>
                    <span>{formatCurrencyFromMinor(total, currency)}</span>
                </div>

                <div className="booking-payment-modal-override">
                    <label className="booking-payment-modal-override-label">
                        <input
                            type="checkbox"
                            checked={useCustom}
                            onChange={e => setUseCustom(e.target.checked)}
                        />
                        Override lesson fee (optional)
                    </label>
                    <p className="booking-payment-modal-override-hint">
                        Overrides the lesson fee only. Venue fee is unchanged unless you edit the booking first. Use to
                        give a discount on the lesson fee.
                    </p>
                    {useCustom && (
                        <div className="booking-payment-modal-custom-amount">
                            <span className="booking-payment-modal-currency-prefix">$</span>
                            <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder={((ep.lessonFee || 0) / 100).toFixed(2)}
                                value={customAmount}
                                onChange={e => setCustomAmount(e.target.value)}
                                className="booking-payment-modal-amount-input"
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderFooter = () => {
        if (step === "qr") {
            // Matches AdminPaymentModal's QR footer: Back to summary + Done. The
            // "Send Email Too" from the QR step was removed — the summary step
            // already has "OK (send email)" and re-opening shows the existing link status.
            return (
                <div className="booking-payment-modal-footer-row">
                    <button className="base-modal-cancel-btn" onClick={() => setStep("summary")}>
                        Back
                    </button>
                    <div className="booking-payment-modal-footer-actions">
                        <button className="base-modal-save-btn" onClick={onClose}>
                            Done
                        </button>
                    </div>
                </div>
            );
        }

        if (confirmMarkPaid) {
            // The rail picker (MarkPaidActions) owns the "Mark paid" action in the body;
            // the footer only needs a way back.
            return (
                <div className="booking-payment-modal-footer-row">
                    <button className="base-modal-cancel-btn" onClick={() => setConfirmMarkPaid(false)} disabled={isMarkingPaid}>
                        Back
                    </button>
                </div>
            );
        }

        return (
            <div className="booking-payment-modal-footer-row">
                <button className="base-modal-cancel-btn" onClick={onClose}>
                    Close
                </button>
                <div className="booking-payment-modal-footer-actions">
                    {/* Always available for unpaid bookings — marking a booking paid
                        externally (cash / EFTPOS / bank transfer) is a core instructor
                        workflow, not gated by the bank-transfer addon offering flag
                        (which may be unset for some orgs after the free-tier pivot). */}
                    <button
                        className="base-modal-secondary-btn"
                        onClick={() => setConfirmMarkPaid(true)}
                        disabled={isGenerating || isSendingEmail || isMarkingPaid}
                    >
                        <Icon path={mdiAccountCashOutline} size={1} />
                        Mark paid
                    </button>
                    <button
                        className="base-modal-save-btn"
                        onClick={handleGenerateQr}
                        disabled={isGenerating || isSendingEmail || cardPaymentsBlocked}
                        title={cardPaymentsBlocked ? "Connect your payout account in Manage Finances first" : undefined}
                    >
                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Icon path={mdiQrcode} size={0.9} />
                            {isGenerating ? "Generating..." : "Pay now (QR)"}
                        </span>
                    </button>
                    <button
                        className="base-modal-secondary-btn"
                        onClick={() => handleSendEmail(true)}
                        disabled={isSendingEmail || isGenerating || !recipientEmail || cardPaymentsBlocked}
                        title={cardPaymentsBlocked
                            ? "Connect your payout account in Manage Finances first"
                            : (recipientEmail ? undefined : "No email on file")}
                    >
                        <Icon path={mdiEmailOutline} size={1} />
                        OK (send email)
                    </button>
                </div>
            </div>
        );
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={`Request Payment — ${studentName}`}
            icon={mdiCurrencyUsd}
            maxWidth={620}
            className="booking-payment-modal-overlay-boost"
            footer={<div className="booking-payment-modal">{renderFooter()}</div>}
        >
            <div className="booking-payment-modal">{renderBody()}</div>
        </BaseModal>
    );
}

export default BookingPaymentModal;
